import { omit } from 'es-toolkit';
import { CART_QUERY_KEYS } from './keys';
import type {
  GetCartRequest,
  AddCartItemsRequest,
  AddToCartRequest,
  UpdateCartItemRequest,
  DeleteCartRequest,
  ApplyCouponRequest,
  RemoveCouponRequest,
  GetExpectedCreditRequest,
  GetCartRewardProductsRequest,
} from './types';
import { cartAPIClient } from '../cartAPIClient';

import { cartTokenQueue } from './cartTokenQueue';
import type { QueryClient } from '@tanstack/react-query';
import { CancelledError, mutationOptions, queryOptions } from '@tanstack/react-query';
import type { CartResponseResponse } from '../../../model/cart/cart';
import { persistCartSessionKey } from '../../../utils/sessionKeyPersistence';
import { convertCookieToKey } from '../../../utils/browser/cookie';

export type CartServiceMutationProps = GetCartRequest & {
  queryClient: QueryClient;
};

const setGeneratedSessionKey = (data: CartResponseResponse['generatedSessionKey']) => {
  if (data?.key && data?.value) {
    // Persist via registered persistence (web: cookie, RN: AsyncStorage, etc.)
    void persistCartSessionKey({
      name: data.key,
      value: data.value,
    });
  }
};

// Cart writes below put the cart straight into the cache, so nothing under the cart root is
// refetched. Server-derived credits and reward quotes must be re-asked after every write.
const invalidateCartPreviews = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.expectedCredit() });
  void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.rewardProducts() });
};

const applyCartMutationResponse = (
  queryClient: QueryClient,
  { currency, sessionKey }: Pick<CartServiceMutationProps, 'currency' | 'sessionKey'>,
  data: CartResponseResponse,
) => {
  cartTokenQueue.enqueue(data.cartInfo.cartToken);

  // A mutation fired before a country change can resolve after it. Writing that
  // stale-currency cart under the new currency's key poisons the cache, and since
  // setQueryData resets freshness it also suppresses the corrective refetch.
  const responseMatchesRequestedCurrency = data.cart.currency.currency === currency;
  if (responseMatchesRequestedCurrency) {
    // Cancel older reads synchronously before publishing the newer mutation result.
    const queryKey = CART_QUERY_KEYS.getCart({ currency, sessionKey });
    void queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.setQueryData(queryKey, data);
    const generatedSessionKey = convertCookieToKey({
      name: data.generatedSessionKey?.key,
      value: data.generatedSessionKey?.value,
    });
    // The hook adopts this identity after success. Seed that exact key first so
    // a newly issued guest session does not temporarily unmount the cart content.
    if (generatedSessionKey && generatedSessionKey !== sessionKey) {
      const generatedQueryKey = CART_QUERY_KEYS.getCart({ currency, sessionKey: generatedSessionKey });
      void queryClient.cancelQueries({ queryKey: generatedQueryKey, exact: true });
      queryClient.setQueryData(generatedQueryKey, data);
    }
  } else {
    void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.default() });
  }
  setGeneratedSessionKey(data.generatedSessionKey);
  invalidateCartPreviews(queryClient);
};

export const TEMP_ITEM_KEY = 'TEMP_PRODUCT';
const REWARD_PRODUCTS_STALE_TIME = 30_000;
const REWARD_PRODUCTS_GC_TIME = 60_000;

async function getReadyCartToken({ queryClient, currency, sessionKey }: CartServiceMutationProps): Promise<string> {
  const token = cartTokenQueue.peek();
  if (token) {
    return token;
  }

  // An add can precede the initial cart response. Reuse that query's in-flight
  // request; never send an additive write until the server has identified the bag.
  const cart = await queryClient.fetchQuery({
    ...CART_QUERIES.getCart({ currency, sessionKey }),
    staleTime: 0,
  });
  const readyToken = cartTokenQueue.peek();
  if (!readyToken || readyToken !== cart.cartInfo.cartToken || cart.cart.currency.currency !== currency) {
    throw new Error('Could not prepare your bag. Please try again.');
  }
  return readyToken;
}

export const CART_QUERIES = {
  getRewardProducts: (params: GetCartRewardProductsRequest, cartSnapshotKey: string) =>
    queryOptions({
      queryKey: CART_QUERY_KEYS.getRewardProducts(params, cartSnapshotKey),
      queryFn: async ({ signal }) => ({
        ...(await cartAPIClient.getRewardProducts(
          { ...params, cartToken: params.cartToken ?? cartTokenQueue.peek() },
          { signal },
        )),
        cartSnapshotKey,
      }),
      // Retain prefetches for the same shopper and cart snapshot while the cart is closed.
      // Cart writes still invalidate quotes immediately, independently of this freshness window.
      staleTime: REWARD_PRODUCTS_STALE_TIME,
      gcTime: REWARD_PRODUCTS_GC_TIME,
      retry: false,
      placeholderData: (previous, previousQuery) => {
        const previousKey = previousQuery?.queryKey;
        const nextKey = CART_QUERY_KEYS.getRewardProducts(params, cartSnapshotKey);
        // The snapshot includes customer/auth identity as well as cart contents. Never
        // carry a quote across identity switches, even if a session key is retained.
        if (!previous || !previousKey || previousKey.slice(0, -1).some((value, index) => value !== nextKey[index])) {
          return undefined;
        }
        try {
          const before = JSON.parse(previous.cartSnapshotKey);
          const after = JSON.parse(cartSnapshotKey);
          if (['country', 'actorId', 'isAuthenticated', 'customerId'].some((key) => before[key] !== after[key])) {
            return undefined;
          }
        } catch {
          return undefined;
        }
        return previous;
      },
    }),

  getCart: ({ currency, cartToken, sessionKey }: GetCartRequest) => {
    return queryOptions({
      queryKey: CART_QUERY_KEYS.getCart({ currency, sessionKey }),
      // Adopting the response's identity belongs to the request, not to reading the cache:
      // `select` re-runs on every cached read, so a logout that clears the queue could see it
      // refilled with the signed-out user's token, and a cancelled refetch still applied its
      // response. Passing the signal lets a cancelled request be dropped before it answers.
      queryFn: async ({ signal }) => {
        const cart = await cartAPIClient.getCart({ sessionKey, currency, cartToken }, { signal });
        if (signal.aborted) {
          throw new CancelledError();
        }
        cartTokenQueue.enqueue(cart.cartInfo.cartToken);
        setGeneratedSessionKey(cart.generatedSessionKey);
        return cart;
      },
    });
  },

  addToCart: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: async (payload: AddToCartRequest) => {
        const cartToken = await getReadyCartToken({ queryClient, currency, sessionKey });
        return cartAPIClient.addToCart({
          ...payload,
          currency,
          sessionKey,
          cartToken,
        });
      },
      mutationKey: CART_QUERY_KEYS.addToCart(),
      // A lost response may already have changed the cart. Never replay an additive write automatically.
      retry: false,
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  /**
   * Bulk add. Unlike `addToCart` this sends no `sessionKey` — `/cart/items` rejects unknown
   * properties and reads the cart from `cartToken` alone, the same token every other cart call
   * already depends on.
   */
  addItemsToCart: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: async (payload: AddCartItemsRequest) => {
        const cartToken = await getReadyCartToken({ queryClient, currency, sessionKey });
        return cartAPIClient.addItemsToCart({
          ...payload,
          currency,
          cartToken,
        });
      },
      mutationKey: CART_QUERY_KEYS.addItemsToCart(),
      retry: false,
      onSuccess: (data) => {
        // `results` reports on this request, not the cart — it must not leak into the cart cache.
        applyCartMutationResponse(queryClient, { currency, sessionKey }, omit(data, ['results']));
      },
    });
  },

  getExpectedCredit: ({
    currency,
    sessionKey,
    appliedCreditAmount,
  }: GetExpectedCreditRequest & Pick<GetCartRequest, 'sessionKey'>) => {
    return queryOptions({
      queryKey: CART_QUERY_KEYS.getExpectedCredit({ currency, sessionKey, appliedCreditAmount }),
      // The endpoint identifies the cart from the session cookie and cart token; `sessionKey`
      // is deliberately not part of the request.
      queryFn: () => cartAPIClient.getExpectedCredit({ currency, appliedCreditAmount }),
    });
  },

  updateCartItem: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: UpdateCartItemRequest) => {
        return cartAPIClient.updateCartItem({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.updateCartItem(),

      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  deleteCartItem: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: DeleteCartRequest) => {
        return cartAPIClient.deleteCartItem({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.removeCartItem(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  applyCoupon: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: ApplyCouponRequest) => {
        const cartToken = request.cartToken || cartTokenQueue.peek();

        return cartAPIClient.applyCoupon({
          ...request,
          sessionKey,
          cartToken,
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.applyCoupon(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  removeCoupon: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: RemoveCouponRequest) => {
        return cartAPIClient.removeCoupon({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.removeCoupon(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },
};
